import { en, type MessageKey, type Messages } from './i18n/en.js';
import { ar } from './i18n/ar.js';

/**
 * Translation.
 *
 * Two catalogues, one shape. `ar.ts` is typed as an exact mirror of `en.ts`,
 * so completeness is a compile error rather than something a reviewer notices
 * or a customer does. There is no runtime fallback to English: a fallback is
 * how a half-translated interface ships and stays half-translated, because
 * nothing ever fails.
 */

import { DEFAULT_FORMAT_CONTEXT, type FormatContext } from './format.js';

export type { MessageKey, Messages };
export type { FormatContext };
export { en, ar };

export const LANGUAGES = ['en', 'ar'] as const;
export type Language = (typeof LANGUAGES)[number];

export const CATALOGUES: Record<Language, Messages> = { en, ar };

export const LANGUAGE_DIRECTION: Record<Language, 'ltr' | 'rtl'> = { en: 'ltr', ar: 'rtl' };

/** The default. Arabic is a toggle, off until a workspace or person turns it on. */
export const DEFAULT_LANGUAGE: Language = 'en';

export interface TranslateOptions {
  /**
   * Values substituted into {placeholders}.
   *
   * Strings only, on purpose: a number reaching here would be formatted by
   * template interpolation instead of by the one formatter, which is how a
   * total ends up rendered three different ways on three different screens.
   */
  values?: Record<string, string>;
}

const PLACEHOLDER = /\{([a-zA-Z0-9_]+)\}/g;

/**
 * Whether this string is a key the catalogue actually has.
 *
 * Exists so a caller that builds a key from data can refuse rather than guess.
 * The navigation did the opposite: it mapped area keys to message keys with a
 * switch and a `default`, so two areas nobody had written labels for rendered
 * as "Setup" - a plausible, confident, wrong word, in the one place a person
 * navigates from (P-39).
 */
export function isMessageKey(candidate: string): candidate is MessageKey {
  return Object.prototype.hasOwnProperty.call(en, candidate);
}

export function translate(
  language: Language,
  key: MessageKey,
  options: TranslateOptions = {},
): string {
  const template = CATALOGUES[language][key];
  if (options.values === undefined) {
    assertNoUnfilledPlaceholders(key, template, {});
    return template;
  }
  const values = options.values;
  assertNoUnfilledPlaceholders(key, template, values);
  return template.replace(PLACEHOLDER, (_match, name: string) => values[name] ?? '');
}

/**
 * Refuses a message with a placeholder nothing filled.
 *
 * "Hi ," is the classic preventable failure - a greeting whose token resolved
 * to nothing and was rendered anyway. It fails here, at the call site, rather
 * than in a customer's inbox.
 */
function assertNoUnfilledPlaceholders(
  key: MessageKey,
  template: string,
  values: Record<string, string>,
): void {
  const missing: string[] = [];
  for (const match of template.matchAll(PLACEHOLDER)) {
    const name = match[1]!;
    const value = values[name];
    if (value === undefined || value === '') missing.push(name);
  }
  if (missing.length > 0) {
    throw new Error(
      `Message '${key}' has ${missing.length} placeholder(s) with nothing to put in them: ${missing.join(', ')}. ` +
        `Pass a value, or use a message that does not need one.`,
    );
  }
}

/** Every key both catalogues carry. Derived, and it asserts its own size. */
export function messageKeys(): MessageKey[] {
  return Object.keys(en).sort() as MessageKey[];
}

/**
 * Labels an engine value.
 *
 * `qualified` becomes "Qualified". The engine's spelling never reaches a
 * screen, and a value with no label is reported rather than shown - a status
 * chip reading "in_progress" is a defect, not a shortfall in translation.
 */
export function labelEnum(
  language: Language,
  value: string,
  labels: Record<string, Partial<Record<Language, string>>>,
): { text: string; labelled: boolean } {
  const entry = labels[value];
  const translated = entry?.[language];
  if (translated) return { text: translated, labelled: true };
  const english = entry?.en;
  if (english) return { text: english, labelled: true };
  // `labelled: false` is what check:strings and the render check look for: an
  // engine value that reached a screen is reported, not quietly tidied up.
  return { text: humanise(value), labelled: false };
}

/** A last resort, so an unlabelled value is at least readable while it is fixed. */
export function humanise(value: string): string {
  const spaced = value.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * The formatting context a language implies.
 *
 * Here rather than in the web app's provider, because a test that wants to
 * know what a screen will render has to build the same context - and two
 * constructions of "what does ar-AE format like" drift the first time one of
 * them gains a numbering system. The provider calls this; the test harness
 * calls this; there is one answer.
 */
export function formatContextFor(
  language: Language,
  over: Partial<FormatContext> = {},
): FormatContext {
  return {
    ...DEFAULT_FORMAT_CONTEXT,
    locale: language === 'ar' ? 'ar-AE' : 'en-GB',
    numerals: 'latn',
    ...over,
  };
}
